import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import {
  CreateOperationProfileRequest,
  OperationProfile,
} from '../../operation-profile/models/operation-profile.model';
import { TemplateAdoptionResponse } from '../models/template-adoption.model';
import { TemplateVersionResponse } from '../models/template-version.model';

/**
 * Platform operation-profile templates (super-admin). Unlike tenant profiles
 * these have no per-operation endpoints: PUT replaces the whole template —
 * profile fields and the entire operations list — in a single call, so the
 * caller always sends the full operations array it wants to persist.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminOperationTemplateService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/admin/operation-profile-template`;

  getTemplates(): Observable<ApiResponse<OperationProfile[]>> {
    return this.http.get<ApiResponse<OperationProfile[]>>(this.baseUrl);
  }

  getTemplateById(id: number): Observable<ApiResponse<OperationProfile>> {
    return this.http.get<ApiResponse<OperationProfile>>(`${this.baseUrl}/${id}`);
  }

  createTemplate(request: CreateOperationProfileRequest): Observable<ApiResponse<OperationProfile>> {
    return this.http.post<ApiResponse<OperationProfile>>(this.baseUrl, request);
  }

  updateTemplate(
    id: number,
    request: CreateOperationProfileRequest,
  ): Observable<ApiResponse<OperationProfile>> {
    return this.http.put<ApiResponse<OperationProfile>>(`${this.baseUrl}/${id}`, request);
  }

  deleteTemplate(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  /** Cross-tenant adoption metrics for a template (super-admin). */
  getAdoption(id: number): Observable<ApiResponse<TemplateAdoptionResponse>> {
    return this.http.get<ApiResponse<TemplateAdoptionResponse>>(`${this.baseUrl}/${id}/adoption`);
  }

  /** Fleet-wide adoption: one report per template (super-admin). */
  getAdoptionFleet(): Observable<ApiResponse<TemplateAdoptionResponse[]>> {
    return this.http.get<ApiResponse<TemplateAdoptionResponse[]>>(`${this.baseUrl}/adoption`);
  }

  /** Full version history for a template, newest first (super-admin). */
  getVersions(id: number): Observable<ApiResponse<TemplateVersionResponse[]>> {
    return this.http.get<ApiResponse<TemplateVersionResponse[]>>(`${this.baseUrl}/${id}/versions`);
  }
}
